"use client";

import { getUserReferralCode } from "@/app/_lib/apis/referral";
import { usePassportProvider } from "@/context";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import {
  Body,
  Box,
  ButtCon,
  Button,
  Heading,
  Modal,
  TextArea,
} from "@biom3/react";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@constants";
import { useMemo, useState } from "react";
import useSWRImmutable from "swr/immutable";

type ReferralCode = { referral_code: string };

type ReferralInviteModalProps = {
  visible: boolean;
  onClose: () => void;
  gameUid?: string;
  campaignId?: number;
  campaignPoints?: number;
};

export function ReferralInviteModal({
  visible,
  onClose,
  gameUid,
  campaignId,
  campaignPoints,
}: ReferralInviteModalProps) {
  const { isLoggedIn, passportClient } = usePassportProvider();
  const { trackV3 } = useAnalytics();
  const [isCopying, setIsCopying] = useState(false);

  const { data, error, isLoading } = useSWRImmutable<ReferralCode>(
    visible && campaignId ? ["referral-code", campaignId] : null,
    async () => {
      const token = await passportClient.getAccessToken();
      if (!token) throw new Error("No access token");
      return getUserReferralCode(token, { campaignId });
    },
    { revalidateOnFocus: false },
  );

  const referralCode = data?.referral_code ?? "";
  const isFatesExp = useMemo(() => gameUid === "fates-exp", [gameUid]);

  const shareUrl = useMemo(() => {
    if (!referralCode || typeof window === "undefined" || !gameUid) return "";
    const origin = window.location.origin;
    const url = new URL(`${origin}/games/${gameUid}`);
    url.searchParams.set("referral", referralCode);
    return url.toString();
  }, [referralCode, gameUid]);

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopying(true);
      setTimeout(() => setIsCopying(false), 2000);

      trackV3({
        object: "Quest",
        action: Action.PRESSED,
        extras: {
          campaignId,
          gameUid,
          referralCode,
          control: "CopyReferralURL",
          section: "ReferralInviteModal",
          isLoggedIn: Boolean(isLoggedIn),
        },
      });
    } catch (e) {
      console.error("Failed to copy leaderboard referral link:", e);
    }
  };

  const handleClose = () => {
    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        campaignId: campaignId,
        gameUid: gameUid,
        referralCode: referralCode,
        control: "CloseReferralInviteModal",
        section: "ReferralInviteModal",
        isLoggedIn: Boolean(isLoggedIn),
      },
    });
    onClose();
  };
  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      showBgOverlay={false}
      outsideClicksClose={false}
      testId="referral-invite-modal"
    >
      <Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "base.borderRadius.x10",
          position: "relative",
          overflow: "hidden",
          background: "base.color.brand.2",
          backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/referral-invite-modal-background.svg")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top",
          height: MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
        }}
      >
        {/* Content Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "base.spacing.x14",
            padding: "base.spacing.x12",
            height: "80%",
            justifyContent: "center",
          }}
        >
          {/* Text Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "base.spacing.x6",
            }}
          >
            <Heading
              size="medium"
              weight="bold"
              sx={{
                textAlign: "center",
              }}
            >
              {isFatesExp
                ? "Invite friends for a share of $60k!"
                : `Invite friends to join the leaderboard and both get ${campaignPoints || ""} points`}
            </Heading>
            <Body
              sx={{
                color: "base.color.text.body.secondary",
                textAlign: "center",
                fontSize: "base.text.body.medium.regular.fontSize",
                lineHeight: "base.text.body.small.regular.lineHeight",
              }}
            >
              {isFatesExp
                ? "Share your link with friends. When they accept you both earn 200 gems. Gems are used to win a share of $60k with Immutable each week. The more friends you invite, the more gems you earn!"
                : `Share your link with friends. When they complete their first quest, you both earn ${campaignPoints || ""} points. The more friends you invite, the more points you earn!`}
            </Body>
            {error && (
              <Body
                sx={{
                  color: "base.color.status.error.foreground",
                  textAlign: "center",
                }}
              >
                Failed to load referral code. Please try again.
              </Body>
            )}
          </Box>

          {/* Text Area with Copy Button */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                border: "1px solid rgba(247, 247, 247, 0.64)",
                borderRadius: "base.borderRadius.x4",
                paddingRight: "100px",
                minHeight: "base.spacing.x12",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextArea
                value={isLoading ? "Loading..." : shareUrl}
                disabled
                hideClearValueButton
                sx={{
                  backgroundColor: "base.color.translucent.inverse.1000",
                  paddingRight: "0px", // override Biome default paddingRight
                  overflow: "hidden",
                  maxHeight: "base.spacing.x15",
                  minWidth: "230px",
                  boxShadow: "none",
                  "& textarea": {
                    fontSize: "base.text.body.small.regular.fontSize",
                    lineHeight: "base.text.body.small.regular.lineHeight",
                  },
                }}
              />
            </Box>
            <Button
              variant="primary"
              size="small"
              onClick={handleCopy}
              disabled={!shareUrl || isCopying}
              sx={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
              }}
            >
              <Button.Icon icon={isCopying ? "Tick" : "CopyText"} />
              {isCopying ? "Copied" : "Copy"}
            </Button>
          </Box>
        </Box>

        {/* Close button */}
        <ButtCon
          icon="Close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "base.spacing.x4",
            right: "base.spacing.x4",
          }}
          variant="tertiary"
        />
      </Modal.Content>
    </Modal>
  );
}
